import React, { useState } from 'react';
import { FiX, FiSearch, FiUser, FiAlertCircle } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import './NewMessageModal.css';

/**
 * NewMessageModal Component
 * Allows users to start a new DM conversation by searching for a username
 * Features:
 * - Username search with validation
 * - Live search results
 * - Checks if user has DMs enabled
 * - Opens DirectMessaging window on selection
 */
export default function NewMessageModal({ onClose, onSelectUser }) {
    const { supabase, currentUser } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);

    const handleSearch = async (query) => {
        setSearchQuery(query);
        setError(null);

        if (!query || query.trim().length < 2) {
            setSearchResults([]);
            setHasSearched(false);
            return;
        }

        setLoading(true);
        setHasSearched(true);

        try {
            // Search for users by username or display_name
            const { data, error: searchError } = await supabase
                .from('profiles')
                .select('id, username, display_name, dm_enabled, banned')
                .or(`username.ilike.%${query}%,display_name.ilike.%${query}%`)
                .neq('id', currentUser.id) // Exclude current user
                .limit(10);

            if (searchError) throw searchError;

            // Filter out banned users
            const validUsers = (data || []).filter(user => !user.banned);

            setSearchResults(validUsers);
        } catch (err) {
            console.error('Error searching users:', err);
            setError('Failed to search users. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectUser = (user) => {
        // Check if user has DMs enabled
        if (user.dm_enabled === false) {
            setError(`${user.display_name || user.username} has disabled direct messages.`);
            return;
        }

        // Call parent callback with selected user
        if (onSelectUser) {
            onSelectUser({
                id: user.id,
                username: user.username,
                display_name: user.display_name
            });
        }

        onClose();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch(searchQuery);
        }
    };

    return (
        <div className="new-message-modal-overlay" onClick={onClose}>
            <div className="new-message-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="new-message-header">
                    <h3>New Message</h3>
                    <button className="close-btn" onClick={onClose} aria-label="Close">
                        <FiX />
                    </button>
                </div>

                {/* Search Input */}
                <div className="new-message-search">
                    <FiSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by username or display name..."
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        onKeyPress={handleKeyPress}
                        autoFocus
                        className="search-input"
                    />
                </div>

                {/* Error Message */}
                {error && (
                    <div className="new-message-error">
                        <FiAlertCircle />
                        <span>{error}</span>
                    </div>
                )}

                {/* Search Results */}
                <div className="new-message-results">
                    {loading ? (
                        <div className="results-loading">
                            <div className="spinner"></div>
                            <p>Searching...</p>
                        </div>
                    ) : searchResults.length > 0 ? (
                        <div className="results-list">
                            {searchResults.map((user) => (
                                <div
                                    key={user.id}
                                    className={`user-result-item ${user.dm_enabled === false ? 'disabled' : ''}`}
                                    onClick={() => handleSelectUser(user)}
                                >
                                    <div className="user-avatar">
                                        <FiUser />
                                    </div>
                                    <div className="user-info">
                                        <div className="user-name">
                                            {user.display_name || user.username}
                                        </div>
                                        <div className="user-username">
                                            @{user.username}
                                        </div>
                                    </div>
                                    {user.dm_enabled === false && (
                                        <div className="user-dm-disabled">
                                            <FiAlertCircle />
                                            <span>DMs disabled</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : hasSearched ? (
                        <div className="results-empty">
                            <FiUser className="empty-icon" />
                            <h4>No users found</h4>
                            <p>Try searching by username or display name</p>
                        </div>
                    ) : (
                        <div className="results-prompt">
                            <FiSearch className="prompt-icon" />
                            <h4>Search for a user</h4>
                            <p>Enter at least 2 characters to search</p>
                        </div>
                    )}
                </div>

                {/* Instructions */}
                <div className="new-message-footer">
                    <p className="instructions">
                        Search for a user by their username or display name to start a conversation.
                    </p>
                </div>
            </div>
        </div>
    );
}
